import React, { useMemo, useState } from 'react'
import { Divider, useTheme } from '@material-ui/core'
import { Container, InfrastructureFilterChips } from '../../../components'
import { SchedulerHeaderNav } from './SchedulerHeaderNav'
import { UnitSearchInput } from './UnitSearchInput'
import {
    RateVendorDialog,
    RateVendorTarget,
    isWithinRateVendorsWindow,
} from '../../Workforce/RateVendorDialog'
import { useCompany } from '../../../hooks'

import {
    ApartmentVendor,
    AreaStatusTag,
    BaseWorkorder,
    ChangeOrderStatus,
    ListVendor,
    Schedule,
    Service,
    UnitConfig,
    WorkorderAnalyticDict,
} from '../../../models'
import { Location, LocationSelection } from '../../../store'
import { SchedulerAnalyticsHeadline } from './SchedulerAnalyticsHeadline'
import { ManualUnitReadyMark } from '../unitReady'
import { ScheduleFilterController } from '../../../hooks/useFilter'
import { useSchedulerHeaderStyles } from './styles'
import { ScheduleHeaderTitle } from './ScheduleHeaderTitle'
import { SchedulerHeaderBigButtons } from './SchedulerHeaderBigButtons'
import { useTimezone } from '../../../contexts/timezone'
import { DateTime } from 'luxon'

interface Props {
    schedule?: Schedule
    filterOpen: boolean
    filterController: ScheduleFilterController
    onUnitSearchingChange?: (isSearching: boolean) => void
    locationSelection: LocationSelection
    vendorList: ListVendor[]
    unitConfigList: UnitConfig[]
    serviceList: Service[]
    analyticDict: WorkorderAnalyticDict
    editMode: boolean
    analyticsOpen: boolean
    visibleInfrastructureUnitCount: number
    visibleInfrastructureAreaCount: number
    visibleAreaConfigBreakdown: { [name: string]: number }
    ghostServiceAreasCount: number
    workorderList?: BaseWorkorder[]
    areaStatusTagList?: AreaStatusTag[]
    openProjectNotes: boolean
    fullWorkorderList?: BaseWorkorder[]
    refreshWorkorders: () => void
    setLocationSelection: (location?: Location) => void
    clickAssign: () => void
    clickBudget: () => void
    clickFilter: () => void
    clickAnalytics: () => void
    clickBulkEdit: () => void
    setEditMode: (editMode: boolean) => void
    clickKeyTracking: () => void
    clickSchedule: () => void
    clickScheduleReview: () => void
    clickProjectNotes: () => void
    clickChangeOrder: () => void
    downloadUnitNotesReport: () => void
    bulkLockAreaStatusTags: (locked: boolean) => void
    isSpreadsheetView: boolean
    setIsSpreadsheetView: (value: boolean) => void
    scheduleReviewEnabled: boolean
    dtChainOpen: boolean
    dtChainAlertMode: boolean
    onDtChainOpen: () => void
    onDtChainClose: () => void
    visibleUnitIdSet?: Set<number>
    manualUnitReady?: Map<number, ManualUnitReadyMark>
}

export const SchedulerHeader = (props: Props) => {
    const {
        schedule,
        filterOpen,
        filterController,
        onUnitSearchingChange,
        locationSelection,
        vendorList,
        unitConfigList,
        serviceList,
        analyticDict,
        editMode,
        analyticsOpen,
        visibleInfrastructureUnitCount,
        visibleInfrastructureAreaCount,
        visibleAreaConfigBreakdown,
        ghostServiceAreasCount,
        areaStatusTagList,
        workorderList,
        fullWorkorderList,
        openProjectNotes,
        setLocationSelection,
        isSpreadsheetView,
        setIsSpreadsheetView,
        clickAssign,
        clickBudget,
        clickFilter,
        clickAnalytics,
        clickBulkEdit,
        setEditMode,
        clickKeyTracking,
        clickSchedule,
        clickScheduleReview,
        clickProjectNotes,
        clickChangeOrder,
        downloadUnitNotesReport,
        bulkLockAreaStatusTags,
        refreshWorkorders,
        scheduleReviewEnabled,
        dtChainOpen,
        dtChainAlertMode,
        onDtChainOpen,
        onDtChainClose,
        visibleUnitIdSet,
        manualUnitReady,
    } = props
    const theme = useTheme()
    const classes = useSchedulerHeaderStyles()
    const { currentTimezone } = useTimezone()

    const startDate = schedule?.start_date
        ? DateTime.fromISO(schedule.start_date).setZone(currentTimezone)
        : undefined
    const endDate = schedule?.end_date
        ? DateTime.fromISO(schedule.end_date).setZone(currentTimezone)
        : undefined

    const [viewAnalyticsTab, setViewAnalyticsTab] = useState<number>(0)
    const [rateVendorsOpen, setRateVendorsOpen] = useState(false)
    const [rateVendorTargets, setRateVendorTargets] = useState<
        RateVendorTarget[]
    >([])
    const { getApartmentVendorList } = useCompany({})

    const showRateVendors = isWithinRateVendorsWindow(schedule?.end_date)

    const buildRateVendorTargets = (
        aptVendors: ApartmentVendor[],
    ): RateVendorTarget[] => {
        const aptByVendorId: Record<number, ApartmentVendor> = {}
        aptVendors.forEach((aptVendor) => {
            aptByVendorId[aptVendor.vendor.id] = aptVendor
        })

        const vendorById: Record<number, ListVendor> = {}
        vendorList.forEach((vendor) => {
            vendorById[vendor.id] = vendor
        })

        const scheduleVendorIds = new Set<number>()
        ;(fullWorkorderList || workorderList || []).forEach((wo) => {
            if (wo.vendor_id != null) {
                scheduleVendorIds.add(wo.vendor_id)
            }
        })

        const ids =
            scheduleVendorIds.size > 0
                ? Array.from(scheduleVendorIds)
                : vendorList.map((v) => v.id)

        return ids
            .map((id): RateVendorTarget | null => {
                const vendor = vendorById[id]
                const apt = aptByVendorId[id]
                const name = vendor?.name || apt?.vendor.name
                if (!name) {
                    return null
                }
                return {
                    id,
                    name,
                    professionalism: apt?.professionalism,
                    work_quality: apt?.work_quality,
                }
            })
            .filter((v): v is RateVendorTarget => v != null)
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    const pendingChangeOrderCount = useMemo(
        () =>
            workorderList?.reduce((prevCount, wo) => {
                return (prevCount +=
                    wo.changeorder_set?.reduce((coCount, co) => {
                        return (coCount +=
                            co.status === ChangeOrderStatus.PENDING ? 1 : 0)
                    }, 0) ?? 0)
            }, 0) ?? 0,
        [workorderList],
    )

    return (
        <Container style={{ flexDirection: 'column' }}>
            {/* Main Header */}
            <Container className={classes.mainHeader}>
                {/* Top row: Title | Icons + Key Stats + Analytics box */}
                <Container className={classes.mainHeaderTopRow}>
                    <ScheduleHeaderTitle
                        schedule={schedule}
                        startDate={startDate}
                        endDate={endDate}
                        setIsSpreadsheetView={setIsSpreadsheetView}
                        refreshWorkorders={refreshWorkorders}
                        clickSchedule={clickSchedule}
                        clickScheduleReview={clickScheduleReview}
                        scheduleReviewEnabled={scheduleReviewEnabled}
                    />
                    <RateVendorDialog
                        open={rateVendorsOpen}
                        onClose={() => setRateVendorsOpen(false)}
                        title="Rate vendors for this project"
                        vendors={rateVendorTargets}
                        scheduleId={schedule?.id}
                        onSaved={() => {
                            getApartmentVendorList()
                        }}
                    />
                    <SchedulerHeaderNav
                        filterOpen={filterOpen}
                        editMode={editMode}
                        clickAssign={clickAssign}
                        clickBudget={clickBudget}
                        clickFilter={clickFilter}
                        clickAnalytics={clickAnalytics}
                        analyticsOpen={analyticsOpen}
                        visibleInfrastructureUnitCount={
                            visibleInfrastructureUnitCount
                        }
                        visibleInfrastructureAreaCount={
                            visibleInfrastructureAreaCount
                        }
                        visibleAreaConfigBreakdown={visibleAreaConfigBreakdown}
                        ghostServiceAreasCount={ghostServiceAreasCount}
                        setEditMode={setEditMode}
                        clickKeyTracking={clickKeyTracking}
                        schedule={schedule}
                        workorderList={workorderList}
                        areaStatusTagList={areaStatusTagList}
                        openProjectNotes={openProjectNotes}
                        clickProjectNotes={clickProjectNotes}
                        clickChangeOrder={clickChangeOrder}
                        downloadUnitNotesReport={downloadUnitNotesReport}
                        bulkLockAreaStatusTags={bulkLockAreaStatusTags}
                        refreshWorkorders={refreshWorkorders}
                        isSpreadsheetView={isSpreadsheetView}
                        setIsSpreadsheetView={setIsSpreadsheetView}
                        viewAnalyticsTab={viewAnalyticsTab}
                        setViewAnalyticsTab={setViewAnalyticsTab}
                        clickScheduleReview={clickScheduleReview}
                        scheduleReviewEnabled={scheduleReviewEnabled}
                        dtChainOpen={dtChainOpen}
                        dtChainAlertMode={dtChainAlertMode}
                        onDtChainOpen={onDtChainOpen}
                        onDtChainClose={onDtChainClose}
                    />
                </Container>
                {/* Bottom row: Big buttons under title + icons */}
                <Container className={classes.mainHeaderBigButtonsRow}>
                    <SchedulerHeaderBigButtons
                        schedule={schedule}
                        editMode={editMode}
                        filterOpen={filterOpen}
                        clickSchedule={clickSchedule}
                        clickBudget={clickBudget}
                        clickAssign={clickAssign}
                        clickFilter={clickFilter}
                        clickBulkEdit={clickBulkEdit}
                        clickChangeOrder={clickChangeOrder}
                        pendingChangeOrderCount={pendingChangeOrderCount}
                        showRateVendors={showRateVendors}
                        onClickRateVendors={() => {
                            getApartmentVendorList()
                                .then((list) => {
                                    setRateVendorTargets(
                                        buildRateVendorTargets(list),
                                    )
                                    setRateVendorsOpen(true)
                                })
                                .catch(() => {
                                    setRateVendorTargets(
                                        buildRateVendorTargets([]),
                                    )
                                    setRateVendorsOpen(true)
                                })
                        }}
                    />
                </Container>
            </Container>

            {/* Divide main header from Unit Search and Filter Chips */}
            <Divider />

            {/* Schedule Analytics */}
            <SchedulerAnalyticsHeadline
                analyticDict={analyticDict}
                scheduleDetail={schedule}
                areaStatusTagList={areaStatusTagList}
                workorderList={workorderList}
                setInfrastructureFilter={
                    filterController.setInfrastructureFilter
                }
                fullWorkorderList={fullWorkorderList}
                viewAnalyticsTab={viewAnalyticsTab}
                visibleUnitIdSet={visibleUnitIdSet}
                manualUnitReady={manualUnitReady}
            />
            <Divider />

            {/* Filter Chips */}
            <Container
                style={{
                    flex: 1,
                    marginTop: theme.spacing(1),
                }}
            >
                {/* Unit Search - native input, zero React updates on keystroke */}
                <UnitSearchInput
                    filterController={filterController}
                    onSearchingChange={onUnitSearchingChange}
                />
                <InfrastructureFilterChips
                    filter={filterController.filter}
                    locationSelection={locationSelection}
                    setLocationSelection={setLocationSelection}
                    containerStyle={{ flex: 1 }}
                    hideInspectionChips
                    vendorList={vendorList}
                    unitConfigList={unitConfigList}
                    serviceList={serviceList}
                    disableChips={editMode}
                />
            </Container>
        </Container>
    )
}
